import axios from 'axios'
import jwt_decode from 'jwt-decode'

export default {
  namespaced: true,
  state: {
    status: '',
    token: localStorage.getItem('token') || '',
    refreshToken: localStorage.getItem('refreshToken') || '',
    refreshTokenInterval: null,

    clients: JSON.parse(localStorage.getItem('clients')) || [],

    userClientMarkets:
      (localStorage.getItem('userClientMarkets') !== 'undefined' &&
        JSON.parse(localStorage.getItem('userClientMarkets'))) ||
      [],
    defaultClientId: localStorage.getItem('defaultClientId') || null,
    selectedClientId: localStorage.getItem('selectedClientId') || null,
  },
  mutations: {
    auth_request(state) {
      state.status = 'loading'
    },
    auth_success(
      state,
      {token, refreshToken, clients, userClientMarkets, defaultClientId, selectedClientId},
    ) {
      state.status = 'success'
      state.token = token
      state.refreshToken = refreshToken

      state.clients = clients
      state.userClientMarkets = userClientMarkets
      state.defaultClientId = defaultClientId
      state.selectedClientId = selectedClientId

      localStorage.setItem('token', token)
      localStorage.setItem('refreshToken', refreshToken)
      localStorage.setItem('clients', JSON.stringify(clients))
      localStorage.setItem('userClientMarkets', JSON.stringify(userClientMarkets))
      localStorage.setItem('defaultClientId', defaultClientId)
      localStorage.setItem('selectedClientId', selectedClientId)
    },
    auth_error(state) {
      state.status = 'error'
    },
    logout(state) {
      clearInterval(state.refreshTokenInterval)
      state.status = ''
      state.token = ''
      state.refreshToken = ''
      state.refreshTokenInterval = null
      state.clients = []
      state.userClientMarkets = []
      state.defaultClientId = null
      state.selectedClientId = null

      localStorage.removeItem('lastActivity')
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('clients')
      localStorage.removeItem('userClientMarkets')
      localStorage.removeItem('defaultClientId')
      localStorage.removeItem('selectedClientId')
    },
    setSelectedClientId(state, {selectedClientId}) {
      state.selectedClientId = selectedClientId
    },
    setUserClientMarkets(state, {data}) {
      state.userClientMarkets = data
    },
    start_refresh_token_interval(state, {immediate}) {
      if (immediate) {
        this.dispatch('authentication/getNewAccessToken')
      }

      state.refreshTokenInterval = setInterval(() => {
        this.dispatch('authentication/getNewAccessToken')
      }, 15 * 1000 * 60) // 15 minutes
    },
  },
  actions: {
    login({commit}, user) {
      return new Promise((resolve, reject) => {
        commit('auth_request')
        axios({
          url: '/api/Token',
          data: user,
          method: 'POST',
        })
          .then(resp => {
            const {
              token,
              refreshToken,
              clients,
              userClientMarkets,
              defaultClientId,
              applicationName,
              applicationOrigin,
              applicationRevision,
              applicationVersion,
              applicationConfiguration,
              applicationPaths,
              markets,
            } = resp.data

            this.dispatch('app/setConfiguration', {
              applicationName,
              applicationOrigin,
              applicationRevision,
              applicationVersion,
              applicationConfiguration,
              applicationPaths,
              markets,
            })

            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

            this.dispatch('authentication/startRefreshTokenInterval')

            const selectedClientId = defaultClientId
            commit('auth_success', {
              token,
              refreshToken,
              clients,
              userClientMarkets,
              defaultClientId,
              selectedClientId,
            })

            localStorage.setItem('lastActivity', Date.now())
            resolve(resp)
          })
          .catch(err => {
            commit('auth_error')
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            reject(err)
          })
      })
    },
    logout({commit, dispatch}) {
      return new Promise(resolve => {
        dispatch(
          'app/cleanApplicationConfiguration',
          {},
          {
            root: true,
          },
        )
        commit('logout')
        localStorage.removeItem('lastActivity')
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('clients')
        localStorage.removeItem('userClientMarkets')
        localStorage.removeItem('defaultClientId')
        localStorage.removeItem('selectedClientId')
        localStorage.removeItem('applicationPaths')

        delete axios.defaults.headers.common['Authorization']
        resolve()
      })
    },
    async setSelectedClientId({commit}, {selectedClientId}) {
      const {data} = await axios.get('/api/Token/getUserClientMarkets', {
        params: {
          clientId: selectedClientId,
        },
      })

      commit('setSelectedClientId', {
        selectedClientId,
      })
      commit('setUserClientMarkets', {
        data,
      })
      localStorage.setItem('userClientMarkets', JSON.stringify(data))
      localStorage.setItem('selectedClientId', selectedClientId)
    },
    startRefreshTokenInterval({commit}, {immediate} = {}) {
      commit('start_refresh_token_interval', {
        immediate,
      })
    },
    async getNewAccessToken({commit, state, getters}) {
      const {refreshToken} = state
      if (!refreshToken) {
        return
      }

      const username = getters.currentUsername
      let selectedClientId = parseInt(getters.selectedClientId)
      commit('auth_request')
      const resp = await axios({
        url: '/api/Token/refreshToken',
        data: {
          username,
          refreshToken,
          selectedClientId,
        },
        method: 'POST',
      }).catch(async ({response}) => {
        if (response.data == 'Could not create token') {
          this.dispatch('authentication/logout')
          const router = await import('../router')
          router.default.push('/login')
          return
        }
      })

      if (resp == null) {
        return
      }

      if (resp.data == null) {
        this.dispatch('authentication/logout')
        const router = await import('../router')
        router.default.push('/login')
        return
      }

      const {
        token,
        clients,
        userClientMarkets,
        defaultClientId,
        applicationName,
        applicationOrigin,
        applicationRevision,
        applicationVersion,
        applicationConfiguration,
        applicationPaths,
        markets,
      } = resp.data

      this.dispatch('app/setConfiguration', {
        applicationName,
        applicationOrigin,
        applicationRevision,
        applicationVersion,
        applicationConfiguration,
        applicationPaths,
        markets,
      })

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      commit('auth_success', {
        token,
        refreshToken,
        clients,
        userClientMarkets,
        defaultClientId,
        selectedClientId,
      })
    },
    async changePassword(_store, {currentPassword, newPassword}) {
      const response = await axios
        .put('/api/accounts/changeMyPassword', {
          currentPassword,
          newPassword,
        })
        .catch(error => error)

      if (response.status === 400) {
        let errorMessage =
          response.data.Error != null ? response.data.Error[0] : 'Error changing password'
        return {success: false, message: errorMessage}
      }

      if (response.status === 200) {
        return {success: true, message: 'Password changed successfully'}
      }

      return {success: false, message: 'There was a problem'}
    },
  },
  getters: {
    isLoggedIn: state => {
      return !!state.token
    },
    currentUsername: (state, getters) => {
      if (!getters.token) return null
      return (
        getters.token['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
        null
      )
    },
    currentRole: (state, getters) => {
      if (!getters.token) return null
      return getters.token['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || null
    },
    authStatus: state => state.status,
    token: state => {
      if (!state.token) return null

      return jwt_decode(state.token)
    },
    tokenString: state => state.token,
    refreshToken: state => state.refreshToken,
    clients: state => state.clients,
    userClientMarkets: state => state.userClientMarkets,
    defaultClientId: state => state.defaultClientId,
    selectedClientId: state => state.selectedClientId,
    selectedClient: state => state.clients.find(c => c.clientId == state.selectedClientId),
  },
}
